import { loadConfig } from './config.js';
import { getAccessToken } from './auth.js';
import { resolveActiveOrg } from './active-org.js';
import { ApiError } from './errors.js';

export class ApiClient {
  readonly baseUrl: string;
  private _tokenPromise: Promise<string | null> | undefined;

  constructor() {
    const config = loadConfig();
    this.baseUrl = config.api_url;
  }

  private getToken(): Promise<string | null> {
    if (!this._tokenPromise) this._tokenPromise = getAccessToken();
    return this._tokenPromise;
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const token = await this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  async request<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
    // Active-org selection (human/JWT only — backend ignores it for rec_ tokens, whose org is
    // bound into the token, and validates membership before honoring it).
    const activeOrg = resolveActiveOrg().orgId;
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers: {
        ...(await this.authHeaders()),
        'Content-Type': 'application/json',
        'X-Rekor-Client': 'cli',
        ...(activeOrg ? { 'X-Rekor-Active-Org': activeOrg } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    // Read the body once as text — error/204 responses may be empty or non-JSON, and
    // an unconditional res.json() would throw before we can map the status to an ApiError.
    const text = await res.text();
    let parsed: { data?: T; error?: { message?: string; code?: string } } | undefined;
    if (text) {
      try {
        parsed = JSON.parse(text) as typeof parsed;
      } catch {
        parsed = undefined;
      }
    }

    if (!res.ok) {
      throw new ApiError(
        res.status,
        parsed?.error?.message ?? `HTTP ${res.status}`,
        parsed?.error?.code,
        parsed,
        this.baseUrl,
      );
    }
    return parsed?.data as T;
  }

  async uploadFile(url: string, body: BodyInit, contentType: string): Promise<void> {
    const res = await fetch(url, {
      method: 'PUT',
      headers: {
        ...(await this.authHeaders()),
        'Content-Type': contentType,
      },
      body,
    });
    if (!res.ok) {
      throw new ApiError(res.status, `Upload failed: HTTP ${res.status}`);
    }
  }
}
